import type {
    KontentMedia,
    KontentRichText,
} from '../../../types/KontentProps';
import {
    getMediaData,
    optimizeKontentImageUrl,
    getDisplayDimensions,
} from '../Global/DataUtils';

/** Merges Kontent’s three headline RTE lines into one value for a single RichText render. */
export function mergeFeatureShowcaseHeadlineLines(
    line1: KontentRichText | undefined,
    line2: KontentRichText | undefined,
    line3: KontentRichText | undefined
): KontentRichText | undefined {
    const lines = [line1, line2, line3].filter((l): l is KontentRichText =>
        Boolean(l && typeof l.value === 'string' && l.value.trim())
    );
    if (lines.length === 0) return undefined;
    if (lines.length === 1) return lines[0];
    const base = lines[0];
    return {
        ...base,
        value: lines.map((l) => l.value).join(''),
        modular_content: lines
            .flatMap((l) => l.modular_content ?? [])
            .filter(Boolean),
        links: lines.flatMap((l) => l.links ?? []),
        images: lines.flatMap((l) => l.images ?? []),
    };
}

export type FeatureShowcaseCategoryMediaItem = KontentMedia['value'][number] & {
    id?: string;
    elements?: KontentMedia['value'][number]['elements'] & {
        caption?: { value?: string | null };
    };
};

export const AUTO_ADVANCE_MS = 7000;

export const MOBILE_SLIDE_MS = 380;

export const DEFAULT_GRADIENT_START = '#EF6825';
export const DEFAULT_GRADIENT_END = '#B22E34';

export interface NormalizedSlide {
    id: string;
    imageUrl: string;
    imageSrcSet: string;
    imageWidth: number | undefined;
    imageHeight: number | undefined;
    label: string;
    alt: string;
}

export function normalizeFeatureShowcaseSlides(
    categories: KontentMedia | undefined
): NormalizedSlide[] {
    const categoriesLinkedValue = categories?.value;
    if (categoriesLinkedValue == null) return [];

    const items: FeatureShowcaseCategoryMediaItem[] = (
        Array.isArray(categoriesLinkedValue)
            ? categoriesLinkedValue
            : [categoriesLinkedValue]
    ) as FeatureShowcaseCategoryMediaItem[];
    if (!items.length) return [];

    return items.map((item, index) => {
        const media = getMediaData({
            value: [item],
        } as KontentMedia);
        const url = media?.url || '';
        const cap = item.elements?.caption?.value;
        const label =
            cap ||
            media?.name ||
            item.elements?.file?.value?.[0]?.name ||
            'Slide';
        const stableId = item.id || `slide-${index}`;
        const alt = media?.description || media?.name || cap || label || '';
        const srcSetWidths = [375, 480, 552, 640, 748];
        const imageSrcSet = srcSetWidths
            .map(
                (w) =>
                    `${optimizeKontentImageUrl(url, {
                        w,
                        fm: 'webp',
                        q: 80,
                    })} ${w}w`
            )
            .join(', ');

        const displayDims = getDisplayDimensions(
            media?.width,
            media?.height,
            748
        );
        return {
            id: stableId,
            imageUrl: optimizeKontentImageUrl(url, {
                w: 748,
                fm: 'webp',
                q: 80,
            }),
            imageSrcSet,
            imageWidth: displayDims.width,
            imageHeight: displayDims.height,
            label,
            alt,
        };
    });
}
